import { METRICS_TABS, MetricsTabId } from 'src/containers/metrics/constants';
import {
  MetricsNotification,
  MetricsNotificationSeverity,
} from 'src/containers/metrics/notifications';

import { monitoringAPI } from './monitoring';

export type AlertTab =
  | 'scoring'
  | 'queues'
  | 'sagemaker'
  | 'dbhealth'
  | 'sentry';

export type AlertSeverity = 'critical' | 'warning';

export interface AlertItemDto {
  targetId: string;
  target: string;
  ruleKey: string;
  severity: AlertSeverity;
  message: string;
  since: string;
}

export interface TabAlertsDto {
  tab: AlertTab;
  count: number;
  critical: number;
  warning: number;
  worst: AlertSeverity | null;
  evaluatedAt: string | null;
  stale: boolean;
  error: string | null;
  alerts: AlertItemDto[];
}

export interface AlertsSummaryDto {
  generatedAt: string;
  tabs: TabAlertsDto[];
}

export interface MappedTabAlerts {
  tabId: MetricsTabId;
  count: number;
  worst: AlertSeverity | null;
  evaluatedAt: string | null;
  stale: boolean;
  error: string | null;
  notifications: MetricsNotification[];
}

export interface MappedAlertsSummary {
  generatedAt: string;
  tabs: MappedTabAlerts[];
  notifications: MetricsNotification[];
}

const ALERT_TAB_TO_METRICS: Record<AlertTab, MetricsTabId> = {
  scoring: 'scoring',
  queues: 'queue',
  sagemaker: 'sagemaker',
  dbhealth: 'database',
  sentry: 'sentry',
};

const ALERT_TABS = Object.keys(ALERT_TAB_TO_METRICS);

export const ALERTS_SUMMARY_POLL_MS = 60_000;

const isAlertTab = (value: string): value is AlertTab =>
  ALERT_TABS.includes(value);

const toSeverity = (severity: string): MetricsNotificationSeverity =>
  severity === 'critical' ? 'critical' : 'warning';

const toTimestamp = (value: string, fallback: string) => {
  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  const generated = Date.parse(fallback);
  return Number.isNaN(generated) ? Date.now() : generated;
};

const mapTabAlerts = (
  tab: TabAlertsDto,
  generatedAt: string
): MappedTabAlerts => {
  const tabId = ALERT_TAB_TO_METRICS[tab.tab];

  return {
    tabId,
    count: tab.count,
    worst: tab.worst,
    evaluatedAt: tab.evaluatedAt,
    stale: tab.stale,
    error: tab.error,
    notifications: (tab.alerts || []).map((alert) => ({
      id: `${tabId}:${alert.targetId}:${alert.ruleKey}:${alert.since}`,
      tabId,
      title: alert.target || alert.ruleKey,
      message: alert.message,
      severity: toSeverity(alert.severity),
      timestamp: toTimestamp(alert.since, generatedAt),
    })),
  };
};

const missingTabAlerts = (tabId: MetricsTabId): MappedTabAlerts => ({
  tabId,
  count: 0,
  worst: null,
  evaluatedAt: null,
  stale: false,
  error: 'Alerts for this section were not included in the summary.',
  notifications: [],
});

export const mapAlertsSummary = (
  summary: AlertsSummaryDto
): MappedAlertsSummary => {
  const byTab = new Map<MetricsTabId, MappedTabAlerts>();

  summary.tabs.forEach((tab) => {
    if (!isAlertTab(tab.tab)) {
      return;
    }

    byTab.set(
      ALERT_TAB_TO_METRICS[tab.tab],
      mapTabAlerts(tab, summary.generatedAt)
    );
  });

  const tabs = METRICS_TABS.map(
    (tab) => byTab.get(tab.id) || missingTabAlerts(tab.id)
  );

  return {
    generatedAt: summary.generatedAt,
    tabs,
    notifications: tabs.flatMap((tab) => tab.notifications),
  };
};

export const fetchAlertsSummary = async (): Promise<MappedAlertsSummary> => {
  const { data } = await monitoringAPI.get<AlertsSummaryDto>('/alerts/summary');
  return mapAlertsSummary(data);
};

export const ALERT_HISTORY_HOURS = 24;

export interface AlertHistoryTabDto {
  tab: AlertTab;
  count: number;
  critical: number;
  warning: number;
}

export interface AlertHistoryItemDto {
  tab: AlertTab;
  targetId: string;
  target: string;
  ruleKey: string;
  severity: AlertSeverity;
  message: string;
  startedAt: string;
  endedAt?: string | null;
  durationMinutes?: number | null;
}

export interface AlertHistoryDto {
  from: string;
  to: string;
  hours: number;
  recordedSince: string | null;
  lastRecordedAt: string | null;
  stale: boolean;
  tabs: AlertHistoryTabDto[];
  alerts: AlertHistoryItemDto[];
}

export interface MappedAlertHistory {
  from: string;
  to: string;
  hours: number;
  recordedSince: string | null;
  lastRecordedAt: string | null;
  stale: boolean;
  tabs: MappedTabAlerts[];
  notifications: MetricsNotification[];
}

const worstFromCounts = (
  critical: number,
  warning: number
): AlertSeverity | null => {
  if (critical > 0) {
    return 'critical';
  }

  if (warning > 0) {
    return 'warning';
  }

  return null;
};

const toOptionalTimestamp = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const mapAlertsHistory = (
  history: AlertHistoryDto
): MappedAlertHistory => {
  const notifications = (history.alerts || []).flatMap((alert) => {
    if (!isAlertTab(alert.tab)) {
      return [];
    }

    const tabId = ALERT_TAB_TO_METRICS[alert.tab];
    const endedAt = toOptionalTimestamp(alert.endedAt);

    return [
      {
        id: `${tabId}:${alert.targetId}:${alert.ruleKey}:${alert.startedAt}:${
          alert.endedAt || 'active'
        }`,
        tabId,
        title: alert.target || alert.ruleKey,
        message: alert.message,
        severity: toSeverity(alert.severity),
        timestamp: toTimestamp(alert.startedAt, history.to || history.from),
        history: {
          endedAt,
          durationMinutes:
            typeof alert.durationMinutes === 'number' &&
            Number.isFinite(alert.durationMinutes)
              ? alert.durationMinutes
              : null,
        },
      },
    ];
  });

  const byTab = new Map<MetricsTabId, AlertHistoryTabDto>();
  (history.tabs || []).forEach((tab) => {
    if (!isAlertTab(tab.tab)) {
      return;
    }

    byTab.set(ALERT_TAB_TO_METRICS[tab.tab], tab);
  });

  const tabs = METRICS_TABS.map((tab) => {
    const source = byTab.get(tab.id);
    const tabNotifications = notifications.filter(
      (item) => item.tabId === tab.id
    );
    const derivedCritical = tabNotifications.filter(
      (item) => item.severity === 'critical'
    ).length;
    const derivedWarning = tabNotifications.filter(
      (item) => item.severity === 'warning'
    ).length;

    return {
      tabId: tab.id,
      count: source?.count ?? tabNotifications.length,
      worst: source
        ? worstFromCounts(source.critical, source.warning)
        : worstFromCounts(derivedCritical, derivedWarning),
      evaluatedAt: null,
      stale: Boolean(history.stale),
      error: null,
      notifications: tabNotifications,
    };
  });

  return {
    from: history.from,
    to: history.to,
    hours: history.hours || ALERT_HISTORY_HOURS,
    recordedSince: history.recordedSince || null,
    lastRecordedAt: history.lastRecordedAt || null,
    stale: Boolean(history.stale),
    tabs,
    notifications,
  };
};

export const fetchAlertsHistory = async (
  hours = ALERT_HISTORY_HOURS
): Promise<MappedAlertHistory> => {
  const { data } = await monitoringAPI.get<AlertHistoryDto>('/alerts/history', {
    params: { hours },
  });
  return mapAlertsHistory(data);
};
